import React, { useMemo, useState, Fragment, lazy } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SupplierLayout } from '../../components/SupplierLayout';
import { useSupplierStore, Job, Driver } from '../../lib/supplierStore';
import { RoutePreviewMap } from '../../components/RoutePreviewMap';
import { AskCustomerAiSlider } from '../../components/AskCustomerAiSlider';
import { toast } from 'sonner';
import { Calendar, Clock, MapPin, Map as MapIcon, Truck, User, AlertTriangle, CheckCircle2, Search, ChevronRight, ChevronLeft, ChevronDown, ChevronUp, ArrowRight, X, UserX, MessageCircle, Send, CalendarClock, ExternalLink, PlayCircle, Flag, Image as ImageIcon, ZoomIn, Sparkles } from 'lucide-react';
// Add chat message type
interface ChatMessage {
  id: string;
  from: 'admin' | 'customer';
  text: string;
  time: string;
  proposedTime?: number; // hour, for propose-time messages
  isAccepted?: boolean; // customer reply that accepts the new time
}
// ... at top-level helpers, add ...
const LIFECYCLE_STAGES: Array<{
  id: 'accepted' | 'in_progress' | 'at_pickup' | 'picked' | 'at_dropoff' | 'completed';
  label: string;
}> = [{
  id: 'accepted',
  label: 'Accepted'
}, {
  id: 'in_progress',
  label: 'In progress'
}, {
  id: 'at_pickup',
  label: 'At pickup'
}, {
  id: 'picked',
  label: 'Picked'
}, {
  id: 'at_dropoff',
  label: 'At dropoff'
}, {
  id: 'completed',
  label: 'Completed'
}];
function getStatusPill(status: string): {
  label: string;
  className: string;
} {
  switch (status) {
    case 'unassigned':
      return {
        label: 'Unassigned',
        className: 'bg-amber-50 text-amber-700'
      };
    case 'pending_acceptance':
      return {
        label: 'Pending driver',
        className: 'bg-yellow-50 text-yellow-700'
      };
    case 'accepted':
      return {
        label: 'Accepted',
        className: 'bg-blue-50 text-blue-700'
      };
    case 'in_progress':
      return {
        label: 'In progress',
        className: 'bg-purple-50 text-purple-700'
      };
    case 'at_pickup':
      return {
        label: 'At pickup',
        className: 'bg-indigo-50 text-indigo-700'
      };
    case 'picked':
      return {
        label: 'Picked',
        className: 'bg-cyan-50 text-cyan-700'
      };
    case 'at_dropoff':
      return {
        label: 'At dropoff',
        className: 'bg-teal-50 text-teal-700'
      };
    case 'completed':
      return {
        label: 'Completed',
        className: 'bg-green-50 text-green-700'
      };
    default:
      return {
        label: status,
        className: 'bg-gray-100 text-gray-600'
      };
  }
}
function formatTime(hour: number) {
  const h = Math.floor(hour);
  const m = (hour - h) * 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${m === 0 ? '00' : m} ${ampm}`;
}
function DriverScheduleTimeline({
  driver,
  date,
  proposedStart,
  proposedDuration
}: {
  driver: Driver;
  date: string;
  proposedStart: number;
  proposedDuration: number;
}) {
  const {
    getDriverSchedule
  } = useSupplierStore();
  const schedule = getDriverSchedule(driver.id, date);
  // Simple timeline from 6 AM to 10 PM (16 hours)
  const timelineStart = 6;
  const timelineEnd = 22;
  const timelineHours = timelineEnd - timelineStart;
  const getLeftPct = (hour: number) => Math.max(0, Math.min(100, (hour - timelineStart) / timelineHours * 100));
  const getWidthPct = (duration: number) => Math.min(100, duration / timelineHours * 100);
  return <div className="mt-3" data-id="element-2573">
      <div className="flex justify-between text-[10px] text-gray-400 mb-1 px-1" data-id="element-2574">
        <span data-id="element-2575">6 AM</span>
        <span data-id="element-2576">12 PM</span>
        <span data-id="element-2577">6 PM</span>
        <span data-id="element-2578">10 PM</span>
      </div>
      <div className="relative h-6 bg-gray-100 rounded-md overflow-hidden" data-id="element-2579">
        {/* Existing Jobs */}
        {schedule.map(job => <div key={job.id} className="absolute top-0 bottom-0 bg-gray-400 border-r border-white/50" style={{
        left: `${getLeftPct(job.startHour)}%`,
        width: `${getWidthPct(job.durationHours)}%`
      }} title={`${job.id}: ${formatTime(job.startHour)} - ${formatTime(job.startHour + job.durationHours)}`} data-id="element-2580" />)}
        {/* Proposed Job */}
        <div className="absolute top-0 bottom-0 bg-brand-500/80 border-2 border-brand-500 rounded-sm z-10" style={{
        left: `${getLeftPct(proposedStart)}%`,
        width: `${getWidthPct(proposedDuration)}%`
      }} data-id="element-2581" />
      </div>
    </div>;
}
function CalendarPopover({
  month,
  setMonth,
  selected,
  onSelect,
  onClear,
  jobCountForDate
}: {
  month: Date;
  setMonth: (d: Date) => void;
  selected: Date | null;
  onSelect: (d: Date) => void;
  onClear: () => void;
  jobCountForDate: (d: Date) => number;
}) {
  const today = useMemo(() => {
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    return todayDate;
  }, []);
  const year = month.getFullYear();
  const monthIdx = month.getMonth();
  const firstOfMonth = new Date(year, monthIdx, 1);
  const startWeekday = firstOfMonth.getDay(); // 0 (Sun) - 6 (Sat)
  const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
  // Build a 6-row x 7-col grid
  const cells: Array<Date | null> = [];
  for (let i = 0; i < startWeekday; i++) {
    cells.push(null);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(new Date(year, monthIdx, d));
  }
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }
  const isSameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const goPrev = () => setMonth(new Date(year, monthIdx - 1, 1));
  const goNext = () => setMonth(new Date(year, monthIdx + 1, 1));
  return <div data-id="element-2582">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 px-1" data-id="element-2583">
        <button onClick={goPrev} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" aria-label="Previous month" data-id="element-2584">
          <ChevronLeft className="w-4 h-4 text-gray-600" data-id="element-2585" />
        </button>
        <span className="font-bold text-sm text-gray-900" data-id="element-2586">
          {firstOfMonth.toLocaleDateString([], {
          month: 'long',
          year: 'numeric'
        })}
        </span>
        <button onClick={goNext} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" aria-label="Next month" data-id="element-2587">
          <ChevronRight className="w-4 h-4 text-gray-600" data-id="element-2588" />
        </button>
      </div>
      {/* Weekday header */}
      <div className="grid grid-cols-7 mb-1" data-id="element-2589">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <div key={i} className="text-center text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-2590">
            {d}
          </div>)}
      </div>
      {/* Day grid */}
      <div className="grid grid-cols-7 gap-1" data-id="element-2591">
        {cells.map((d, i) => {
        if (!d) return <div key={i} className="aspect-square" data-id="element-2592" />;
        const isToday = isSameDay(d, today);
        const isSelected = selected ? isSameDay(d, selected) : false;
        const count = jobCountForDate(d);
        return <button key={i} onClick={() => onSelect(d)} className={`relative aspect-square flex flex-col items-center justify-center rounded-lg text-xs font-bold transition-all ${isSelected ? 'bg-gray-900 text-white shadow-md' : isToday ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-200 hover:bg-brand-100' : count > 0 ? 'bg-gray-50 text-gray-900 hover:bg-gray-100' : 'text-gray-400 hover:bg-gray-50'}`} data-id="element-2593">
              <span className="leading-none" data-id="element-2594">{d.getDate()}</span>
              {count > 0 && <span className={`mt-0.5 text-[9px] font-bold leading-none px-1 py-0.5 rounded-full ${isSelected ? 'bg-white/20 text-white' : 'bg-brand-500 text-white'}`} data-id="element-2595">
                  {count}
                </span>}
            </button>;
      })}
      </div>
      {/* Footer */}
      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between" data-id="element-2596">
        <span className="text-[10px] font-medium text-gray-500" data-id="element-2597">
          Numbers show jobs scheduled
        </span>
        {selected && <button onClick={onClear} className="text-[11px] font-bold text-gray-600 hover:text-gray-900 transition-colors" data-id="element-2598">
            Clear
          </button>}
      </div>
    </div>;
}
export function SupplierDispatcher() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedJobId = searchParams.get('jobId');
  const {
    jobs,
    drivers,
    trucks,
    assignJob,
    reassignJob,
    checkConflict,
    updateJobSchedule,
    unassignJob,
    updateJobStatus
  } = useSupplierStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [reassignPrompt, setReassignPrompt] = useState<{
    driverId: string;
    truckId: string;
  } | null>(null);
  const [assignedFlash, setAssignedFlash] = useState<{
    driverName: string;
    reassigned?: boolean;
  } | null>(null);
  const [jobFilter, setJobFilter] = useState<'all' | 'new' | 'assigned' | 'in_progress'>('all');
  // Date filter: null = All, otherwise a specific Date.
  const [dateFilter, setDateFilter] = useState<Date | null>(null);
  const [calendarOpen, setCalendarOpen] = useState<boolean>(false);
  const [calendarMonth, setCalendarMonth] = useState<Date>(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [jobSearch, setJobSearch] = useState('');
  const [chatOpen, setChatOpen] = useState<boolean>(false);
  const [mapOpen, setMapOpen] = useState<boolean>(false);
  const [askAiOpen, setAskAiOpen] = useState<boolean>(false);
  const [photoLightboxIndex, setPhotoLightboxIndex] = useState<number | null>(null);
  const [chatThreads, setChatThreads] = useState<Record<string, ChatMessage[]>>({});
  const [chatInput, setChatInput] = useState('');
  const [proposeTimeOpen, setProposeTimeOpen] = useState<boolean>(false);
  const [proposedHour, setProposedHour] = useState<number>(10);
  // Conflict expansion state — set of driverIds expanded
  const [expandedConflicts, setExpandedConflicts] = useState<Set<string>>(new Set());
  const toggleConflictExpand = (driverId: string) => {
    setExpandedConflicts(prev => {
      const next = new Set(prev);
      if (next.has(driverId)) next.delete(driverId);else next.add(driverId);
      return next;
    });
  };
  // Derived data — must be computed BEFORE any helpers that reference selectedJob.
  const IN_PROGRESS_STATUSES = ['in_progress', 'at_pickup', 'picked', 'at_dropoff'];
  const activeJobs = jobs.filter(j => j.status !== 'completed');
  const newJobs = activeJobs.filter(j => j.status === 'unassigned');
  const assignedJobs = activeJobs.filter(j => ['pending_acceptance', 'accepted'].includes(j.status));
  const inProgressJobs = activeJobs.filter(j => IN_PROGRESS_STATUSES.includes(j.status));
  const baseFilteredJobs = jobFilter === 'new' ? newJobs : jobFilter === 'assigned' ? assignedJobs : jobFilter === 'in_progress' ? inProgressJobs : activeJobs;
  // Map a job's scheduledDate ('today'/'tomorrow') to a real Date for comparison.
  const isSameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const jobToRealDate = (scheduledDate: string): Date => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    if (scheduledDate === 'tomorrow') d.setDate(d.getDate() + 1);
    return d;
  };
  // Apply date filter
  const dateFilteredJobs = dateFilter ? baseFilteredJobs.filter(j => isSameDay(jobToRealDate(j.scheduledDate), dateFilter)) : baseFilteredJobs;
  // Job count per day (used by calendar popover).
  const jobCountForDate = (d: Date): number => activeJobs.filter(j => isSameDay(jobToRealDate(j.scheduledDate), d)).length;
  // Apply text search
  const q = jobSearch.trim().toLowerCase();
  const filteredJobs = q ? baseFilteredJobs.filter(j => {
    return j.id.toLowerCase().includes(q) || j.serviceType.toLowerCase().includes(q) || j.pickup.toLowerCase().includes(q) || j.dropoff.toLowerCase().includes(q) || (j.customerName || '').toLowerCase().includes(q) || (j.customerPhone || '').toLowerCase().includes(q);
  }) : baseFilteredJobs;
  const selectedJob = activeJobs.find(j => j.id === selectedJobId);
  const currentThread = selectedJob ? chatThreads[selectedJob.id] || [] : [];
  // Seed initial thread on first open per job
  const ensureSeededThread = (jobId: string, serviceType: string) => {
    if (chatThreads[jobId]) return;
    const seeded: ChatMessage[] = [{
      id: `m-${Date.now()}-1`,
      from: 'customer',
      text: `Hi, just confirming my ${serviceType.toLowerCase()} booking — let me know if there are any changes.`,
      time: '9:42 AM'
    }];
    setChatThreads(prev => ({
      ...prev,
      [jobId]: seeded
    }));
  };
  const openChat = () => {
    if (!selectedJob) return;
    ensureSeededThread(selectedJob.id, selectedJob.serviceType);
    setChatOpen(true);
  };
  const sendMessage = (text: string, proposedTime?: number) => {
    if (!selectedJob) return;
    const msg: ChatMessage = {
      id: `m-${Date.now()}`,
      from: 'admin',
      text,
      time: new Date().toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit'
      }),
      proposedTime
    };
    setChatThreads(prev => ({
      ...prev,
      [selectedJob.id]: [...(prev[selectedJob.id] || []), msg]
    }));
    // Simulate customer auto-reply
    const jobId = selectedJob.id;
    window.setTimeout(() => {
      const reply: ChatMessage = {
        id: `m-${Date.now()}-r`,
        from: 'customer',
        text: proposedTime !== undefined ? 'Yes, approved — that works for me!' : 'Got it, thanks for the update!',
        time: new Date().toLocaleTimeString([], {
          hour: 'numeric',
          minute: '2-digit'
        }),
        isAccepted: proposedTime !== undefined
      };
      setChatThreads(prev => ({
        ...prev,
        [jobId]: [...(prev[jobId] || []), reply]
      }));
      // Auto-apply the new time when the customer approves it.
      if (proposedTime !== undefined) {
        window.setTimeout(() => {
          updateJobSchedule(jobId, proposedTime);
          const confirmMsg: ChatMessage = {
            id: `m-${Date.now()}-c`,
            from: 'admin',
            text: `Great — I've updated the booking to ${formatTime(proposedTime)}. See you then!`,
            time: new Date().toLocaleTimeString([], {
              hour: 'numeric',
              minute: '2-digit'
            })
          };
          setChatThreads(prev => ({
            ...prev,
            [jobId]: [...(prev[jobId] || []), confirmMsg]
          }));
          toast.success(`Customer approved — rescheduled to ${formatTime(proposedTime)}.`);
        }, 600);
      }
    }, 1500);
  };
  const handleSendChat = () => {
    if (!chatInput.trim()) return;
    sendMessage(chatInput.trim());
    setChatInput('');
  };
  const handleProposeTime = () => {
    if (!selectedJob) return;
    sendMessage(`I'd like to reschedule to ${formatTime(proposedHour)}. Does that work for you?`, proposedHour);
    setProposeTimeOpen(false);
  };
  const applyNewTime = (newHour: number) => {
    if (!selectedJob) return;
    updateJobSchedule(selectedJob.id, newHour);
    toast.success(`Job rescheduled to ${formatTime(newHour)}.`);
    setChatOpen(false);
  };
  const handleSelectJob = (id: string) => {
    setSearchParams({
      jobId: id
    });
  };
  const handleAssign = (driverId: string, truckId: string) => {
    if (!selectedJob) return;
    // If job already assigned to a different driver, prompt for reassignment confirmation.
    if (selectedJob.assignedDriverId && selectedJob.assignedDriverId !== driverId) {
      setReassignPrompt({
        driverId,
        truckId
      });
      return;
    }
    const res = assignJob(selectedJob.id, driverId, truckId);
    if ('conflict' in res && res.conflict.length > 0) {
      toast.error('Cannot assign: Driver has a schedule conflict.');
    } else {
      const assignedDriver = drivers.find(d => d.id === driverId);
      toast.success(`Job assigned to ${assignedDriver?.name || 'driver'} successfully.`);
      // Trigger inline confirmation banner — keep job selected so user can see the result.
      setAssignedFlash({
        driverName: assignedDriver?.name || 'driver'
      });
      window.setTimeout(() => setAssignedFlash(null), 3500);
    }
  };
  const confirmReassign = () => {
    if (!selectedJob || !reassignPrompt) return;
    reassignJob(selectedJob.id, reassignPrompt.driverId, reassignPrompt.truckId);
    const newDriverObj = drivers.find(d => d.id === reassignPrompt.driverId);
    toast.success(`Job reassigned to ${newDriverObj?.name || 'new driver'}.`);
    setAssignedFlash({
      driverName: newDriverObj?.name || 'new driver',
      reassigned: true
    });
    window.setTimeout(() => setAssignedFlash(null), 3500);
    setReassignPrompt(null);
  };
  const [unassignPromptOpen, setUnassignPromptOpen] = useState<boolean>(false);
  const handleUnassign = () => {
    if (!selectedJob) return;
    setUnassignPromptOpen(true);
  };
  const confirmUnassign = () => {
    if (!selectedJob) return;
    unassignJob(selectedJob.id);
    toast.success('Driver unassigned from job.');
    setUnassignPromptOpen(false);
  };
  const handleAdvanceStatus = () => {
    if (!selectedJob) return;
    const idx = LIFECYCLE_STAGES.findIndex(s => s.id === selectedJob.status);
    const nextStage = LIFECYCLE_STAGES[idx + 1];
    if (!nextStage) return;
    updateJobStatus(selectedJob.id, nextStage.id);
    toast.success(`Status updated to ${nextStage.label}.`);
  };
  const previousDriver = reassignPrompt ? drivers.find(d => d.id === selectedJob?.assignedDriverId) : null;
  const newDriver = reassignPrompt ? drivers.find(d => d.id === reassignPrompt.driverId) : null;
  const filteredDrivers = useMemo(() => {
    if (!searchQuery) return drivers;
    const q = searchQuery.toLowerCase();
    return drivers.filter(d => d.name.toLowerCase().includes(q));
  }, [drivers, searchQuery]);
  return <SupplierLayout data-id="element-2599">
      <div className="h-[calc(100vh-80px)] lg:h-screen flex flex-col" data-id="element-2600">
        <div className="flex-1 flex overflow-hidden" data-id="element-2601">
          {/* Left Panel: Job List */}
          <div className="w-full lg:w-1/3 border-r border-gray-200 bg-gray-50 flex flex-col overflow-hidden" data-id="element-2602">
            {/* Header: search + tabs */}
            <div className="bg-white border-b border-gray-200 shrink-0" data-id="element-2603">
              {/* Search + date dropdown */}
              <div className="px-4 pt-4 pb-3 flex items-center gap-2" data-id="element-2604">
                <div className="relative flex-1" data-id="element-2605">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2606" />
                  <input type="text" placeholder="Search jobs..." value={jobSearch} onChange={e => setJobSearch(e.target.value)} className="w-full pl-9 pr-8 py-2 bg-gray-50 border border-transparent rounded-lg text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 focus:bg-white transition-colors" data-id="element-2607" />
                  {jobSearch && <button onClick={() => setJobSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-200" aria-label="Clear search" data-id="element-2608">
                      <X className="w-3 h-3 text-gray-500" data-id="element-2609" />
                    </button>}
                </div>
                {/* Date filter: All / pick date */}
                <div className="relative shrink-0" data-id="element-2610">
                  <div className="flex items-center bg-gray-100 rounded-lg p-0.5" data-id="element-2611">
                    <button onClick={() => {
                    setDateFilter(null);
                  }} className={`px-2.5 py-1.5 rounded-md text-[11px] font-bold transition-all ${dateFilter === null ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-2612">
                      All
                    </button>
                    <button onClick={() => {
                    if (dateFilter) {
                      setCalendarMonth(new Date(dateFilter.getFullYear(), dateFilter.getMonth(), 1));
                    }
                    setCalendarOpen(v => !v);
                  }} className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-bold transition-all ${dateFilter !== null ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-2613">
                      <Calendar className="w-3 h-3" data-id="element-2614" />
                      {dateFilter ? dateFilter.toLocaleDateString([], {
                      month: 'short',
                      day: 'numeric'
                    }) : 'Pick date'}
                    </button>
                  </div>
                  <AnimatePresence data-id="element-2615">
                    {calendarOpen && <>
                        <div className="fixed inset-0 z-30" onClick={() => setCalendarOpen(false)} data-id="element-2616" />
                        <motion.div initial={{
                      opacity: 0,
                      y: -4,
                      scale: 0.98
                    }} animate={{
                      opacity: 1,
                      y: 0,
                      scale: 1
                    }} exit={{
                      opacity: 0,
                      y: -4,
                      scale: 0.98
                    }} transition={{
                      duration: 0.15
                    }} className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-2xl shadow-xl z-40 p-3" data-id="element-2617">
                          <CalendarPopover month={calendarMonth} setMonth={setCalendarMonth} selected={dateFilter} onSelect={d => {
                        setDateFilter(d);
                        setCalendarOpen(false);
                      }} onClear={() => {
                        setDateFilter(null);
                        setCalendarOpen(false);
                      }} jobCountForDate={jobCountForDate} data-id="element-2618" />
                        </motion.div>
                      </>}
                  </AnimatePresence>
                </div>
              </div>
              {/* Status tabs */}
              <div className="flex items-center px-2" data-id="element-2619">
                {[{
                id: 'all',
                label: 'All',
                count: activeJobs.length
              }, {
                id: 'new',
                label: 'New',
                count: newJobs.length
              }, {
                id: 'assigned',
                label: 'Assigned',
                count: assignedJobs.length
              }, {
                id: 'in_progress',
                label: 'Active',
                count: inProgressJobs.length
              }].map(f => {
                const isActive = jobFilter === f.id;
                return <button key={f.id} onClick={() => setJobFilter(f.id as typeof jobFilter)} className={`relative flex-1 px-2 py-2.5 text-xs font-bold transition-colors flex items-center justify-center gap-1.5 ${isActive ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-2620">
                      {f.label}
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isActive ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-2621">
                        {f.count}
                      </span>
                      {isActive && <motion.div layoutId="dispatcher-tab-underline" className="absolute bottom-0 left-2 right-2 h-0.5 bg-gray-900 rounded-full" transition={{
                    type: 'spring',
                    stiffness: 400,
                    damping: 30
                  }} data-id="element-2622" />}
                    </button>;
              })}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-id="element-2623">
              {filteredJobs.length === 0 ? <div className="text-center py-12 text-gray-500 text-sm" data-id="element-2624">
                  {jobFilter === 'new' ? 'No new unassigned jobs.' : jobFilter === 'assigned' ? 'No accepted jobs yet.' : jobFilter === 'in_progress' ? 'No jobs currently in progress.' : 'No active jobs to dispatch.'}
                </div> : filteredJobs.map(job => <button key={job.id} onClick={() => handleSelectJob(job.id)} className={`w-full text-left p-4 rounded-xl border transition-all ${selectedJobId === job.id ? 'bg-white border-brand-500 shadow-md ring-1 ring-brand-500' : 'bg-white border-gray-200 shadow-sm hover:border-brand-300'}`} data-id="element-2625">
                    <div className="flex justify-between items-start mb-2" data-id="element-2626">
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-2627">
                        {job.id}
                      </span>
                      {(() => {
                  const pill = getStatusPill(job.status);
                  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${pill.className}`} data-id="element-2628">
                            {pill.label}
                          </span>;
                })()}
                    </div>
                    <h3 className="font-bold text-gray-900 text-sm mb-1" data-id="element-2629">
                      {job.serviceType}
                    </h3>
                    {job.customerName && <div className="text-xs text-gray-700 font-medium flex items-center gap-1 mb-1.5 truncate" data-id="element-2630">
                        <User className="w-3 h-3 text-gray-400 shrink-0" data-id="element-2631" />
                        {job.customerName}
                      </div>}
                    <div className="text-xs text-gray-500 flex items-center gap-1 mb-2" data-id="element-2632">
                      <Calendar className="w-3.5 h-3.5" data-id="element-2633" />{' '}
                      {job.scheduledDate === 'today' ? 'Today' : 'Tomorrow'} •{' '}
                      <Clock className="w-3.5 h-3.5 ml-1" data-id="element-2634" />{' '}
                      {formatTime(job.startHour)} ({job.durationHours}h)
                    </div>
                    <div className="text-xs text-gray-600 truncate" data-id="element-2635">
                      <MapPin className="w-3 h-3 inline mr-1 text-gray-400" data-id="element-2636" />
                      {job.pickup}
                    </div>
                  </button>)}
            </div>
          </div>
          {/* Right Panel: Assignment */}
          <div className="hidden lg:flex flex-1 bg-white overflow-y-auto flex-col" data-id="element-2637">
            {!selectedJob ? <div className="flex-1 flex flex-col items-center justify-center text-gray-400" data-id="element-2638">
                <Calendar className="w-16 h-16 mb-4 text-gray-200" data-id="element-2639" />
                <p data-id="element-2640">Select a job to view details and assign a driver.</p>
              </div> : <div className="p-8 max-w-3xl mx-auto w-full" data-id="element-2641">
                <AnimatePresence data-id="element-2642">
                  {assignedFlash && <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.25
              }} className="mb-6 flex items-center gap-3 p-4 rounded-2xl bg-green-50 border border-green-200" data-id="element-2643">
                      <div className="w-9 h-9 rounded-full bg-green-500 flex items-center justify-center shrink-0" data-id="element-2644">
                        <CheckCircle2 className="w-5 h-5 text-white" data-id="element-2645" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-2646">
                        <p className="font-bold text-green-900 text-sm" data-id="element-2647">
                          {assignedFlash.reassigned ? 'Job reassigned' : 'Job assigned'}
                        </p>
                        <p className="text-xs text-green-700" data-id="element-2648">
                          {assignedFlash.driverName} is now scheduled for this
                          job. They have been notified.
                        </p>
                      </div>
                      <button onClick={() => setAssignedFlash(null)} className="p-1.5 rounded-full hover:bg-green-100 transition-colors shrink-0" aria-label="Dismiss" data-id="element-2649">
                        <X className="w-4 h-4 text-green-700" data-id="element-2650" />
                      </button>
                    </motion.div>}
                </AnimatePresence>
                {/* Decorated job card */}
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 p-6 shadow-2xl ring-1 ring-white/5" data-id="element-2651">
                  {/* Glow decorations */}
                  <div className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full bg-brand-500/20 blur-3xl" data-id="element-2652" />
                  <div className="pointer-events-none absolute -bottom-32 -left-16 w-72 h-72 rounded-full bg-purple-500/10 blur-3xl" data-id="element-2653" />

                  {/* Title */}
                  <p className="relative text-[10px] font-bold uppercase tracking-widest text-brand-300 mb-2" data-id="element-2654">
                    Selected job
                  </p>
                  {/* Header: service type, id, status pill */}
                  <div className="relative flex items-center gap-3 mb-3 flex-wrap" data-id="element-2655">
                    <h2 className="text-2xl font-display font-bold text-white" data-id="element-2656">
                      {selectedJob.serviceType}
                    </h2>
                    <span className="text-xs font-bold text-white/60 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full" data-id="element-2657">
                      {selectedJob.id}
                    </span>
                    {selectedJob.assignedDriverId && <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider bg-emerald-400/20 text-emerald-200 ring-1 ring-emerald-400/30" data-id="element-2658">
                        <CheckCircle2 className="w-3 h-3" data-id="element-2659" />{' '}
                        {getStatusPill(selectedJob.status).label}
                      </span>}
                  </div>
                  {/* Driver and truck info */}
                  {selectedJob.assignedDriverId && (() => {
                const assignedDriver = drivers.find(d => d.id === selectedJob.assignedDriverId);
                const assignedTruck = trucks.find(t => t.id === selectedJob.assignedTruckId);
                return assignedDriver ? <div className="mt-3 flex items-center gap-3 p-3 rounded-2xl bg-white/5 ring-1 ring-white/10 backdrop-blur-sm" data-id="element-2660">
                          <div className="w-9 h-9 rounded-full bg-brand-500 text-white flex items-center justify-center font-bold shrink-0" data-id="element-2661">
                            {assignedDriver.name.charAt(0)}
                          </div>
                          <div className="min-w-0 flex-1" data-id="element-2662">
                            <p className="text-[10px] font-bold text-brand-300 uppercase tracking-wider" data-id="element-2663">
                              Currently assigned
                            </p>
                            <p className="font-bold text-white text-sm truncate" data-id="element-2664">
                              {assignedDriver.name}
                              {assignedTruck && <span className="font-normal text-white/60 ml-2" data-id="element-2665">
                                  · {assignedTruck.plate} ({assignedTruck.type})
                                </span>}
                            </p>
                          </div>
                          <button onClick={handleUnassign} className="flex items-center gap-1.5 text-brand-600 font-bold text-sm bg-brand-50 px-3 py-1.5 rounded-lg" data-id="element-2666">
                            <UserX className="w-3.5 h-3.5" data-id="element-2667" /> Unassign
                          </button>
                        </div> : null;
              })()}
                  {/* Customer info */}
                  {(selectedJob.customerName || selectedJob.customerPhone) && <div className="mt-4 flex items-center gap-3 p-3 rounded-2xl bg-white/5 ring-1 ring-white/10 backdrop-blur-sm" data-id="element-2668">
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0" data-id="element-2669">
                        <User className="w-5 h-5 text-white/70" data-id="element-2670" />
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-2671">
                        <p className="text-[10px] font-bold text-white/50 uppercase tracking-wider" data-id="element-2672">
                          Customer
                        </p>
                        <p className="font-bold text-white text-sm truncate" data-id="element-2673">
                          {selectedJob.customerName || 'Customer'}
                        </p>
                        {selectedJob.customerPhone && <a href={`tel:${selectedJob.customerPhone}`} className="text-xs text-white/60 hover:text-brand-300 transition-colors" data-id="element-2674">
                            {selectedJob.customerPhone}
                          </a>}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0" data-id="element-2675">
                        <button onClick={() => setAskAiOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-brand-500 to-purple-500 hover:from-brand-400 hover:to-purple-400 text-white text-xs font-bold transition-all shadow-lg shadow-brand-500/30" data-id="element-2676">
                          <Sparkles className="w-3.5 h-3.5" data-id="element-2677" /> Ask via AI
                        </button>
                      </div>
                    </div>}
                  {/* Schedule & route */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 bg-white/5 p-5 rounded-2xl ring-1 ring-white/10 backdrop-blur-sm" data-id="element-2678">
                    <div className="flex items-start gap-3" data-id="element-2679">
                      <div className="w-8 h-8 rounded-lg bg-brand-500/20 text-brand-300 flex items-center justify-center shrink-0" data-id="element-2680">
                        <CalendarClock className="w-4 h-4" data-id="element-2681" />
                      </div>
                      <div className="min-w-0" data-id="element-2682">
                        <p className="text-[10px] font-bold text-white/50 uppercase tracking-wider mb-0.5" data-id="element-2683">
                          Schedule
                        </p>
                        <p className="font-bold text-white" data-id="element-2684">
                          {selectedJob.scheduledDate === 'today' ? 'Today' : 'Tomorrow'}
                          , {formatTime(selectedJob.startHour)}
                        </p>
                        <p className="text-xs text-white/60" data-id="element-2685">
                          Est. duration: {selectedJob.durationHours} hours
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 sm:border-l sm:border-white/10 sm:pl-4" data-id="element-2686">
                      <div className="w-8 h-8 rounded-lg bg-purple-500/20 text-purple-300 flex items-center justify-center shrink-0" data-id="element-2687">
                        <MapPin className="w-4 h-4" data-id="element-2688" />
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-2689">
                        <div className="flex items-center justify-between mb-0.5 gap-2" data-id="element-2690">
                          <p className="text-[10px] font-bold text-white/50 uppercase tracking-wider" data-id="element-2691">
                            Route
                          </p>
                          <button onClick={() => setMapOpen(v => !v)} className="inline-flex items-center gap-1 text-[11px] font-bold text-brand-300 hover:text-brand-200 transition-colors" data-id="element-2692">
                            <MapIcon className="w-3 h-3" data-id="element-2693" />{' '}
                            {mapOpen ? 'Hide map' : 'View on map'}
                          </button>
                        </div>
                        <p className="font-bold text-white truncate" title={selectedJob.pickup} data-id="element-2694">
                          {selectedJob.pickup}
                        </p>
                        <p className="text-xs text-white/60 truncate" title={selectedJob.dropoff} data-id="element-2695">
                          To: {selectedJob.dropoff}
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Map preview */}
                  <AnimatePresence initial={false} data-id="element-2696">
                    {mapOpen && <motion.div initial={{
                  opacity: 0,
                  height: 0,
                  marginTop: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto',
                  marginTop: 16
                }} exit={{
                  opacity: 0,
                  height: 0,
                  marginTop: 0
                }} transition={{
                  duration: 0.25,
                  ease: 'easeOut'
                }} className="overflow-hidden" data-id="element-2697">
                        <RoutePreviewMap pickup={selectedJob.pickup} dropoff={selectedJob.dropoff} singleLocation={!selectedJob.dropoff || selectedJob.pickup === selectedJob.dropoff} eta={`${selectedJob.durationHours}h`} data-id="element-2698" />
                      </motion.div>}
                  </AnimatePresence>

                  {/* Customer photos */}
                  {selectedJob.photos && selectedJob.photos.length > 0 && <div className="mt-6 bg-white border border-gray-200 rounded-2xl p-6" data-id="element-2699">
                      <div className="flex items-center justify-between mb-4" data-id="element-2700">
                        <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2" data-id="element-2701">
                          <ImageIcon className="w-4 h-4 text-gray-500" data-id="element-2702" />
                          Customer photos
                          <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full" data-id="element-2703">
                            {selectedJob.photos.length}
                          </span>
                        </h4>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-2704">
                        {selectedJob.photos.map((src, i) => <button key={i} onClick={() => setPhotoLightboxIndex(i)} className="group relative aspect-square rounded-xl overflow-hidden bg-gray-100 border border-gray-200 hover:border-brand-300 transition-all" data-id="element-2705">
                            <img src={src} alt={`Customer photo ${i + 1}`} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" data-id="element-2706" />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center" data-id="element-2707">
                              <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2708" />
                            </div>
                          </button>)}
                      </div>
                    </div>}

                  {/* Job Lifecycle (progress) */}
                  {selectedJob.assignedDriverId && <div className="mt-6 bg-white border border-gray-200 rounded-2xl p-6" data-id="element-2709">
                      <div className="flex items-center justify-between mb-5" data-id="element-2710">
                        <h4 className="text-sm font-bold text-gray-900" data-id="element-2711">
                          Job Lifecycle
                        </h4>
                        {selectedJob.status === 'completed' ? <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 text-green-700 text-xs font-bold" data-id="element-2712">
                            <CheckCircle2 className="w-3.5 h-3.5" data-id="element-2713" /> Job
                            completed
                          </span> : <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-2714">
                            Driver updates progress
                          </span>}
                      </div>
                      {/* Status stages */}
                      <div className="flex items-start justify-between" data-id="element-2715">
                        {LIFECYCLE_STAGES.map((stage, i) => {
                    const currentIdx = LIFECYCLE_STAGES.findIndex(s => s.id === selectedJob.status);
                    const isComplete = i <= currentIdx;
                    const isCurrent = i === currentIdx;
                    const ts = selectedJob.stageHistory?.[stage.id];
                    const tsLabel = ts ? new Date(ts).toLocaleTimeString([], {
                      hour: 'numeric',
                      minute: '2-digit'
                    }) : null;
                    return <Fragment key={stage.id} data-id="element-2716">
                              <div className="flex flex-col items-center gap-1.5 min-w-0 flex-1" data-id="element-2717">
                                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold transition-colors shrink-0 ${isComplete ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-400'} ${isCurrent ? 'ring-4 ring-brand-100' : ''}`} data-id="element-2718">
                                  {isComplete && !isCurrent ? <CheckCircle2 className="w-3.5 h-3.5" data-id="element-2719" /> : i + 1}
                                </div>
                                <span className={`text-[10px] font-bold text-center whitespace-nowrap ${isCurrent ? 'text-gray-900' : isComplete ? 'text-gray-700' : 'text-gray-400'}`} data-id="element-2720">
                                  {stage.label}
                                </span>
                                <span className={`text-[10px] text-center whitespace-nowrap min-h-[12px] ${isCurrent ? 'text-brand-600 font-bold' : 'text-gray-400'}`} data-id="element-2721">
                                  {tsLabel || ''}
                                </span>
                              </div>
                              {i < LIFECYCLE_STAGES.length - 1 && <div className={`h-0.5 mt-3.5 mx-1 transition-colors flex-1 ${i < currentIdx ? 'bg-brand-500' : 'bg-gray-200'}`} data-id="element-2722" />}
                            </Fragment>;
                  })}
                      </div>
                    </div>}
                </div>
                {/* Driver assignment section */}
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2723">
                  Assign Driver
                </h3>
                {/* Driver search */}
                <div className="relative mb-6" data-id="element-2724">
                  <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2725" />
                  <input type="text" placeholder="Search drivers..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" data-id="element-2726" />
                </div>
                {/* Driver list */}
                <div className="space-y-4" data-id="element-2727">
                  {filteredDrivers.map(driver => {
                const conflicts = checkConflict(driver.id, selectedJob.scheduledDate, selectedJob.startHour, selectedJob.durationHours);
                const hasConflict = conflicts.length > 0;
                const isCurrentlyAssigned = selectedJob.assignedDriverId === driver.id;
                // For demo, just use the driver's assigned truck or default to t1
                const truckId = driver.assignedTruckId || 't1';
                const truck = trucks.find(t => t.id === truckId);
                return <div key={driver.id} className={`p-5 rounded-2xl border ${isCurrentlyAssigned ? 'border-brand-500 bg-brand-50/30' : 'border-gray-200 bg-white'}`} data-id="element-2728">
                        <div className="flex items-center justify-between mb-2" data-id="element-2729">
                          <div className="flex items-center gap-3" data-id="element-2730">
                            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-2731">
                              <User className="w-5 h-5 text-gray-500" data-id="element-2732" />
                            </div>
                            <div data-id="element-2733">
                              <h4 className="font-bold text-gray-900" data-id="element-2734">
                                {driver.name}
                              </h4>
                              <p className="text-xs text-gray-500 flex items-center gap-1" data-id="element-2735">
                                <Truck className="w-3 h-3" data-id="element-2736" />{' '}
                                {truck?.type || 'No truck'} ({truck?.plate})
                              </p>
                            </div>
                          </div>

                          {isCurrentlyAssigned ? <div className="flex items-center gap-1.5 text-brand-600 font-bold text-sm bg-brand-50 px-3 py-1.5 rounded-lg" data-id="element-2737">
                              <CheckCircle2 className="w-4 h-4" data-id="element-2738" /> Assigned
                            </div> : <button onClick={() => handleAssign(driver.id, truckId)} disabled={hasConflict} className={`px-4 py-2 rounded-xl font-bold text-sm transition-colors ${hasConflict ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-900 text-white hover:bg-black'}`} data-id="element-2739">
                              {selectedJob.assignedDriverId ? 'Reassign' : 'Assign'}
                            </button>}
                        </div>

                        {hasConflict && !isCurrentlyAssigned && <div className="mb-3" data-id="element-2740">
                            <button onClick={() => toggleConflictExpand(driver.id)} className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-red-50 hover:bg-red-100 transition-colors text-left" data-id="element-2741">
                              <div className="flex items-center gap-1.5 text-xs font-bold text-red-700" data-id="element-2742">
                                <AlertTriangle className="w-3.5 h-3.5" data-id="element-2743" />
                                {conflicts.length} schedule conflict
                                {conflicts.length === 1 ? '' : 's'}
                              </div>
                              {expandedConflicts.has(driver.id) ? <ChevronUp className="w-4 h-4 text-red-700" data-id="element-2744" /> : <ChevronDown className="w-4 h-4 text-red-700" data-id="element-2745" />}
                            </button>
                            {expandedConflicts.has(driver.id) && <div className="mt-2 space-y-2" data-id="element-2746">
                                {conflicts.map(cj => <div key={cj.id} className="p-3 rounded-lg border border-red-100 bg-white" data-id="element-2747">
                                    <div className="flex items-center justify-between gap-2 mb-1" data-id="element-2748">
                                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-2749">
                                        {cj.id}
                                      </span>
                                      <span className="text-[10px] font-bold text-gray-500" data-id="element-2750">
                                        {formatTime(cj.startHour)} –{' '}
                                        {formatTime(cj.startHour + cj.durationHours)}
                                      </span>
                                    </div>
                                    <h5 className="font-bold text-gray-900 text-sm mb-1" data-id="element-2751">
                                      {cj.serviceType}
                                    </h5>
                                    <p className="text-xs text-gray-600 truncate mb-2" data-id="element-2752">
                                      <MapPin className="w-3 h-3 inline mr-1 text-gray-400" data-id="element-2753" />{' '}
                                      {cj.pickup} → {cj.dropoff}
                                    </p>
                                    <button onClick={() => setSearchParams({
                          jobId: cj.id
                        })} className="inline-flex items-center gap-1 text-xs font-bold text-gray-900 hover:text-brand-600 transition-colors" data-id="element-2754">
                                      View this job{' '}
                                      <ExternalLink className="w-3 h-3" data-id="element-2755" />
                                    </button>
                                  </div>)}
                              </div>}
                          </div>}
                        <DriverScheduleTimeline driver={driver} date={selectedJob.scheduledDate} proposedStart={selectedJob.startHour} proposedDuration={selectedJob.durationHours} data-id="element-2756" />
                      </div>;
              })}
                </div>
              </div>}
          </div>
        </div>
      </div>
      {/* Reassignment Confirmation Modal */}
      <AnimatePresence data-id="element-2757">
        {reassignPrompt && previousDriver && newDriver && selectedJob && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setReassignPrompt(null)} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" data-id="element-2758" />
            <motion.div initial={{
          y: 20,
          opacity: 0,
          scale: 0.98
        }} animate={{
          y: 0,
          opacity: 1,
          scale: 1
        }} exit={{
          y: 10,
          opacity: 0,
          scale: 0.98
        }} transition={{
          type: 'spring',
          stiffness: 350,
          damping: 30
        }} className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-2759">
              <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden pointer-events-auto" data-id="element-2760">
                <div className="px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-2761">
                  <div className="flex items-start justify-between gap-4" data-id="element-2762">
                    <div className="flex items-center gap-3" data-id="element-2763">
                      <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center shrink-0" data-id="element-2764">
                        <AlertTriangle className="w-5 h-5 text-amber-600" data-id="element-2765" />
                      </div>
                      <div data-id="element-2766">
                        <h2 className="text-lg font-display font-bold text-gray-900" data-id="element-2767">
                          Reassign this job?
                        </h2>
                        <p className="text-xs text-gray-500 mt-0.5" data-id="element-2768">
                          {selectedJob.id} — {selectedJob.serviceType}
                        </p>
                      </div>
                    </div>
                    <button onClick={() => setReassignPrompt(null)} className="p-1.5 rounded-full hover:bg-gray-100 transition-colors shrink-0" aria-label="Close" data-id="element-2769">
                      <X className="w-4 h-4 text-gray-500" data-id="element-2770" />
                    </button>
                  </div>
                </div>

                <div className="px-6 py-5" data-id="element-2771">
                  <p className="text-sm text-gray-700 mb-4" data-id="element-2772">
                    This will unassign the job from the current driver and
                    assign it to the new driver.
                  </p>

                  <div className="flex items-center gap-3" data-id="element-2773">
                    {/* Previous Driver */}
                    <div className="flex-1 p-3 rounded-xl border border-red-100 bg-red-50/40" data-id="element-2774">
                      <div className="flex items-center gap-1.5 text-[10px] font-bold text-red-700 uppercase tracking-wider mb-2" data-id="element-2775">
                        <UserX className="w-3 h-3" data-id="element-2776" /> Unassigning
                      </div>
                      <div className="flex items-center gap-2" data-id="element-2777">
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-sm" data-id="element-2778">
                          {previousDriver.name.charAt(0)}
                        </div>
                        <div className="min-w-0" data-id="element-2779">
                          <p className="font-bold text-sm text-gray-900 truncate" data-id="element-2780">
                            {previousDriver.name}
                          </p>
                          <p className="text-[10px] text-gray-500 truncate" data-id="element-2781">
                            {previousDriver.phone}
                          </p>
                        </div>
                      </div>
                    </div>

                    <ArrowRight className="w-5 h-5 text-gray-400 shrink-0" data-id="element-2782" />

                    {/* New Driver */}
                    <div className="flex-1 p-3 rounded-xl border border-green-200 bg-green-50/60" data-id="element-2783">
                      <div className="flex items-center gap-1.5 text-[10px] font-bold text-green-700 uppercase tracking-wider mb-2" data-id="element-2784">
                        <CheckCircle2 className="w-3 h-3" data-id="element-2785" /> Assigning to
                      </div>
                      <div className="flex items-center gap-2" data-id="element-2786">
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-sm" data-id="element-2787">
                          {newDriver.name.charAt(0)}
                        </div>
                        <div className="min-w-0" data-id="element-2788">
                          <p className="font-bold text-sm text-gray-900 truncate" data-id="element-2789">
                            {newDriver.name}
                          </p>
                          <p className="text-[10px] text-gray-500 truncate" data-id="element-2790">
                            {newDriver.phone}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className="text-[11px] text-gray-500 mt-4 leading-relaxed" data-id="element-2791">
                    {previousDriver.name.split(' ')[0]} will be notified that
                    this job has been removed from their schedule.
                  </p>
                </div>

                <div className="px-6 py-4 border-t border-gray-100 flex gap-3 justify-end bg-gray-50/50" data-id="element-2792">
                  <button onClick={() => setReassignPrompt(null)} className="px-5 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl text-sm font-bold transition-colors" data-id="element-2793">
                    Cancel
                  </button>
                  <button onClick={confirmReassign} className="px-5 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl text-sm font-bold transition-colors" data-id="element-2794">
                    Confirm Reassignment
                  </button>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
      {/* Photo Lightbox */}
      <AnimatePresence data-id="element-2795">
        {photoLightboxIndex !== null && selectedJob?.photos && selectedJob.photos[photoLightboxIndex] && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={() => setPhotoLightboxIndex(null)} className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" data-id="element-2796">
              {/* Close button */}
              <button onClick={e => {
          e.stopPropagation();
          setPhotoLightboxIndex(null);
        }} className="absolute top-4 right-4 p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10" aria-label="Close photo viewer" data-id="element-2797">
                <X className="w-5 h-5" data-id="element-2798" />
              </button>
              {/* Counter */}
              <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-white/10 text-white text-xs font-bold z-10" data-id="element-2799">
                {photoLightboxIndex + 1} / {selectedJob.photos.length}
              </div>
              {/* Prev button */}
              {selectedJob.photos.length > 1 && <button onClick={e => {
          e.stopPropagation();
          setPhotoLightboxIndex(photoLightboxIndex === 0 ? selectedJob.photos.length - 1 : photoLightboxIndex - 1);
        }} className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10" aria-label="Previous photo" data-id="element-2800">
                  <ChevronLeft className="w-6 h-6" data-id="element-2801" />
                </button>}
              {/* Image */}
              <motion.img key={photoLightboxIndex} initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.2
        }} src={selectedJob.photos[photoLightboxIndex]} alt={`Customer photo ${photoLightboxIndex + 1}`} onClick={e => e.stopPropagation()} className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" data-id="element-2802" />
              {/* Next button */}
              {selectedJob.photos.length > 1 && <button onClick={e => {
          e.stopPropagation();
          setPhotoLightboxIndex(photoLightboxIndex === selectedJob.photos.length - 1 ? 0 : photoLightboxIndex + 1);
        }} className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10" aria-label="Next photo" data-id="element-2803">
                  <ChevronRight className="w-6 h-6" data-id="element-2804" />
                </button>}
              {/* Thumbnails strip */}
              {selectedJob.photos.length > 1 && <div onClick={e => e.stopPropagation()} className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 px-3 py-2 rounded-2xl bg-white/10 backdrop-blur max-w-[90vw] overflow-x-auto" data-id="element-2805">
                  {selectedJob.photos.map((src, i) => <button key={i} onClick={() => setPhotoLightboxIndex(i)} className={`shrink-0 w-14 h-14 rounded-lg overflow-hidden transition-all ${i === photoLightboxIndex ? 'ring-2 ring-white scale-105' : 'opacity-60 hover:opacity-100'}`} data-id="element-2806">
                      <img src={src} alt="" className="w-full h-full object-cover" data-id="element-2807" />
                    </button>)}
                </div>}
            </motion.div>}
      </AnimatePresence>
      {/* Unassign Confirmation Modal */}
      <AnimatePresence data-id="element-2808">
        {unassignPromptOpen && selectedJob && (() => {
        const currentDriver = drivers.find(d => d.id === selectedJob.assignedDriverId);
        return <>
                <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onClick={() => setUnassignPromptOpen(false)} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" data-id="element-2809" />
                <motion.div initial={{
            y: 20,
            opacity: 0,
            scale: 0.98
          }} animate={{
            y: 0,
            opacity: 1,
            scale: 1
          }} exit={{
            y: 10,
            opacity: 0,
            scale: 0.98
          }} transition={{
            type: 'spring',
            stiffness: 350,
            damping: 30
          }} className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-2810">
                  <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden pointer-events-auto" data-id="element-2811">
                    <div className="px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-2812">
                      <div className="flex items-start justify-between gap-4" data-id="element-2813">
                        <div className="flex items-center gap-3" data-id="element-2814">
                          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center shrink-0" data-id="element-2815">
                            <UserX className="w-5 h-5 text-red-600" data-id="element-2816" />
                          </div>
                          <div data-id="element-2817">
                            <h2 className="text-lg font-display font-bold text-gray-900" data-id="element-2818">
                              Unassign driver?
                            </h2>
                            <p className="text-xs text-gray-500 mt-0.5" data-id="element-2819">
                              {selectedJob.id} — {selectedJob.serviceType}
                            </p>
                          </div>
                        </div>
                        <button onClick={() => setUnassignPromptOpen(false)} className="p-1.5 rounded-full hover:bg-gray-100 transition-colors shrink-0" aria-label="Close" data-id="element-2820">
                          <X className="w-4 h-4 text-gray-500" data-id="element-2821" />
                        </button>
                      </div>
                    </div>

                    <div className="px-6 py-5" data-id="element-2822">
                      <p className="text-sm text-gray-700 mb-4" data-id="element-2823">
                        This will remove the driver from this job. The job will
                        return to the unassigned queue.
                      </p>
                      {currentDriver && <div className="p-3 rounded-xl border border-red-100 bg-red-50/40" data-id="element-2824">
                          <div className="flex items-center gap-1.5 text-[10px] font-bold text-red-700 uppercase tracking-wider mb-2" data-id="element-2825">
                            <UserX className="w-3 h-3" data-id="element-2826" /> Unassigning
                          </div>
                          <div className="flex items-center gap-2" data-id="element-2827">
                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-sm" data-id="element-2828">
                              {currentDriver.name.charAt(0)}
                            </div>
                            <div className="min-w-0" data-id="element-2829">
                              <p className="font-bold text-sm text-gray-900 truncate" data-id="element-2830">
                                {currentDriver.name}
                              </p>
                              <p className="text-[10px] text-gray-500 truncate" data-id="element-2831">
                                {currentDriver.phone}
                              </p>
                            </div>
                          </div>
                        </div>}

                      {currentDriver && <p className="text-[11px] text-gray-500 mt-4 leading-relaxed" data-id="element-2832">
                          {currentDriver.name.split(' ')[0]} will be notified
                          that this job has been removed from their schedule.
                        </p>}
                    </div>

                    <div className="px-6 py-4 border-t border-gray-100 flex gap-3 justify-end bg-gray-50/50" data-id="element-2833">
                      <button onClick={() => setUnassignPromptOpen(false)} className="px-5 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl text-sm font-bold transition-colors" data-id="element-2834">
                        Cancel
                      </button>
                      <button onClick={confirmUnassign} className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-bold transition-colors" data-id="element-2835">
                        Unassign Driver
                      </button>
                    </div>
                  </div>
                </motion.div>
              </>;
      })()}
      </AnimatePresence>
      {/* Chat Panel */}
      <AnimatePresence data-id="element-2836">
        {chatOpen && selectedJob && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setChatOpen(false)} className="fixed inset-0 bg-black/40 z-50" data-id="element-2837" />
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col" data-id="element-2838">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between shrink-0" data-id="element-2839">
                <div data-id="element-2840">
                  <h3 className="font-bold text-gray-900 flex items-center gap-2" data-id="element-2841">
                    <MessageCircle className="w-4 h-4 text-brand-500" data-id="element-2842" /> Chat
                    with customer
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-2843">
                    {selectedJob.id} · {selectedJob.serviceType}
                  </p>
                </div>
                <button onClick={() => setChatOpen(false)} className="p-2 rounded-full hover:bg-gray-100" data-id="element-2844">
                  <X className="w-4 h-4 text-gray-500" data-id="element-2845" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50" data-id="element-2846">
                {currentThread.map(m => <div key={m.id} className={`flex ${m.from === 'admin' ? 'justify-end' : 'justify-start'}`} data-id="element-2847">
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${m.from === 'admin' ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200'}`} data-id="element-2848">
                      <p className="text-sm leading-relaxed" data-id="element-2849">{m.text}</p>
                      <p className={`text-[10px] mt-1 ${m.from === 'admin' ? 'text-white/60' : 'text-gray-400'}`} data-id="element-2850">
                        {m.time}
                      </p>
                      {m.isAccepted && m.from === 'customer' && <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-100 text-green-800 text-[10px] font-bold" data-id="element-2851">
                          <CheckCircle2 className="w-3 h-3" data-id="element-2852" /> Customer approved
                          — schedule updated
                        </div>}
                    </div>
                  </div>)}
              </div>
              {/* Propose time inline */}
              {proposeTimeOpen && <div className="px-4 py-3 border-t border-gray-100 bg-amber-50" data-id="element-2853">
                  <p className="text-xs font-bold text-amber-900 mb-2 flex items-center gap-1.5" data-id="element-2854">
                    <CalendarClock className="w-3.5 h-3.5" data-id="element-2855" /> Propose a new
                    start time
                  </p>
                  <div className="flex items-center gap-2" data-id="element-2856">
                    <select value={proposedHour} onChange={e => setProposedHour(parseFloat(e.target.value))} className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm" data-id="element-2857">
                      {Array.from({
                  length: 28
                }, (_, i) => 7 + i * 0.5).map(h => <option key={h} value={h} data-id="element-2858">
                          {formatTime(h)}
                        </option>)}
                    </select>
                    <button onClick={handleProposeTime} className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-bold" data-id="element-2859">
                      Send proposal
                    </button>
                    <button onClick={() => setProposeTimeOpen(false)} className="px-3 py-2 rounded-lg bg-white border border-gray-200 text-sm" data-id="element-2860">
                      Cancel
                    </button>
                  </div>
                </div>}
              <div className="px-4 py-3 border-t border-gray-100 bg-white shrink-0" data-id="element-2861">
                {!proposeTimeOpen && <button onClick={() => setProposeTimeOpen(true)} className="mb-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 hover:bg-amber-100 text-amber-900 text-xs font-bold" data-id="element-2862">
                    <CalendarClock className="w-3.5 h-3.5" data-id="element-2863" /> Propose new time
                  </button>}
                <div className="flex items-center gap-2" data-id="element-2864">
                  <input type="text" placeholder="Type a message..." value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSendChat()} className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" data-id="element-2865" />
                  <button onClick={handleSendChat} disabled={!chatInput.trim()} className="p-2.5 rounded-xl bg-gray-900 hover:bg-black text-white disabled:bg-gray-200 disabled:text-gray-400 transition-colors" data-id="element-2866">
                    <Send className="w-4 h-4" data-id="element-2867" />
                  </button>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
      {/* AI Ask Slider */}
      <AskCustomerAiSlider open={askAiOpen && !!selectedJob} customerName={selectedJob?.customerName || 'Customer'} contextLabel={selectedJob ? selectedJob.id + ' - ' + selectedJob.serviceType : undefined} onClose={() => setAskAiOpen(false)} data-id="element-2868" />
    </SupplierLayout>;
}